// Packages for one mode (contracts/web.md §5.3): the table, Create / Edit in a dialog (the price is typed in
// major units and sent in minor units, zero-decimal currencies as they are), Archive after a confirm step, and
// "Create default packages" when the mode has no active package. One active package per plan per mode is
// enforced here as well as by the server.

import { useState } from "react";
import { useArchivePackage, useCreateDefaultPackages, useCreatePackage, usePackages, useUpdatePackage } from "../../api/payments";
import type { CurrencyOption, Package, PackageInput, PackagePatch } from "../../api/paymentsSchemas";
import { tableStyles } from "../../components/table";
import { Button, Card, Chip, ConfirmDialog, Dialog, EmptyState, ErrorState, Field, Loading, Segmented, Select, TextInput } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { fmtInt } from "../../lib/format";
import {
  amountProblem,
  CURRENCIES,
  currencyExponent,
  currencyName,
  DEFAULT_PACKAGES,
  formatMoney,
  fromMinor,
  LIMITS,
  MODE_LABEL,
  PLAN_INTERVAL,
  toMinor,
  WEB_PLAN_LABEL,
  WEB_PLANS,
  type PaymentMode,
  type WebPlan,
} from "../../lib/payments";
import pages from "../pages.module.css";
import { PaymentsError, ReadOnly, StripeLink, Switch } from "./components";
import p from "./payments.module.css";

const isLive = (pkg: Package) => pkg.active && !pkg.archivedAt;

const defaultsLine = DEFAULT_PACKAGES.map((d) => `${WEB_PLAN_LABEL[d.plan]} ${formatMoney(d.amount, d.currency)}${d.trialDays ? ` with a ${d.trialDays}-day trial` : ""}`).join(", ");

export function PackagesSection({ mode, connected, canConfigure }: { mode: PaymentMode; connected: boolean; canConfigure: boolean }) {
  const q = usePackages(mode);
  const [dialog, setDialog] = useState<{ kind: "create" } | { kind: "edit"; pkg: Package } | null>(null);
  const [archiving, setArchiving] = useState<Package | null>(null);
  const archive = useArchivePackage();
  const defaults = useCreateDefaultPackages();
  const canChange = canConfigure && connected;

  const actions = canConfigure ? (
    <Button
      size="sm"
      variant="primary"
      disabled={!connected}
      title={connected ? undefined : `Connect ${mode} mode first`}
      onClick={() => setDialog({ kind: "create" })}
      data-testid="create-package"
    >
      Create package
    </Button>
  ) : (
    <ReadOnly testId="packages-read-only">Read-only · packages need the admin role</ReadOnly>
  );

  if (q.isPending)
    return (
      <Card title={`Packages · ${mode} mode`} actions={actions}>
        <Loading rows={4} />
      </Card>
    );
  if (q.isError)
    return (
      <Card title={`Packages · ${mode} mode`} actions={actions}>
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </Card>
    );

  const list = q.data.packages;
  const active = list.filter(isLive);
  return (
    <Card title={`Packages · ${mode} mode`} actions={actions}>
      {canConfigure && !connected ? <p className={p.note}>{MODE_LABEL[mode]} mode isn&rsquo;t connected. Connect it above to create packages.</p> : null}
      {active.length === 0 ? (
        <EmptyState
          title={`No active packages in ${mode} mode`}
          action={
            canChange ? (
              <Button variant="primary" busy={defaults.isPending} onClick={() => defaults.mutate(mode)} data-testid="create-defaults">
                Create default packages
              </Button>
            ) : null
          }
        >
          {canChange ? `${defaultsLine}, all in USD, like the App Store. You can change them afterwards.` : "The web shows no plans in this mode."}
        </EmptyState>
      ) : null}
      <PaymentsError error={defaults.error} context="package" />
      {list.length ? (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table} data-testid="packages">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Name</th>
                <th data-align="right">Price</th>
                <th>Trial</th>
                <th>Status</th>
                <th data-align="right">Subscribers</th>
                <th>Stripe</th>
                {canChange ? <th data-align="right">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {list.map((pkg) => (
                <tr key={pkg.id} data-testid="package-row" data-plan={pkg.plan} data-package={pkg.id}>
                  <td>
                    <strong>{WEB_PLAN_LABEL[pkg.plan]}</strong>
                    <span className={p.sub}>{PLAN_INTERVAL[pkg.plan]}</span>
                  </td>
                  <td>
                    <div className={p.name}>
                      <strong>{pkg.name}</strong>
                      {pkg.badge ? <Chip tone="accent">{pkg.badge}</Chip> : null}
                    </div>
                    {pkg.description ? (
                      <span className={p.sub} title={pkg.description}>
                        {pkg.description}
                      </span>
                    ) : null}
                  </td>
                  <td data-align="right">
                    <span className={p.price} data-testid="package-price">
                      {formatMoney(pkg.amount, pkg.currency)}
                    </span>
                    {pkg.currencyOptions.length ? (
                      <span className={p.sub} title={pkg.currencyOptions.map((o) => formatMoney(o.amount, o.currency)).join(" · ")}>
                        +{pkg.currencyOptions.length} {pkg.currencyOptions.length === 1 ? "currency" : "currencies"}
                      </span>
                    ) : null}
                  </td>
                  <td>{pkg.plan !== "lifetime" && pkg.trialDays ? `${pkg.trialDays} days` : <span className={pages.faint}>—</span>}</td>
                  <td>
                    {pkg.archivedAt ? (
                      <Chip tone="muted" title={`Archived ${formatDateTime(pkg.archivedAt)}`}>
                        archived
                      </Chip>
                    ) : pkg.active ? (
                      <Chip tone="success">active</Chip>
                    ) : (
                      <Chip tone="warning">inactive</Chip>
                    )}
                  </td>
                  <td data-align="right">{fmtInt(pkg.subscribers)}</td>
                  <td>
                    {pkg.stripeUrl ? <StripeLink href={pkg.stripeUrl}>Product</StripeLink> : <span className={`mono ${pages.faint}`}>{pkg.stripePriceId ?? "—"}</span>}
                  </td>
                  {canChange ? (
                    <td data-align="right">
                      {pkg.archivedAt ? null : (
                        <div className={p.rowActions}>
                          <Button size="sm" variant="ghost" onClick={() => setDialog({ kind: "edit", pkg })} aria-label={`Edit ${pkg.name}`}>
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              archive.reset();
                              setArchiving(pkg);
                            }}
                            aria-label={`Archive ${pkg.name}`}
                          >
                            Archive
                          </Button>
                        </div>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <p className={p.tableNote}>
        One active package per plan in each mode. Archived packages stop selling; their subscribers keep renewing.
      </p>

      {dialog ? (
        <PackageDialog
          key={dialog.kind === "edit" ? dialog.pkg.id : "create"}
          mode={mode}
          packages={list}
          editing={dialog.kind === "edit" ? dialog.pkg : null}
          onClose={() => setDialog(null)}
        />
      ) : null}
      <ConfirmDialog
        open={archiving !== null}
        title={`Archive “${archiving?.name ?? ""}”?`}
        confirmLabel="Archive"
        tone="danger"
        busy={archive.isPending}
        onCancel={() => setArchiving(null)}
        onConfirm={() => archiving && archive.mutate({ id: archiving.id, mode }, { onSuccess: () => setArchiving(null) })}
      >
        <p className={pages.prose}>
          The Stripe product and price stop selling, and the web stops offering this {archiving ? WEB_PLAN_LABEL[archiving.plan].toLowerCase() : ""} plan. Existing
          subscriptions continue. An archived package can&rsquo;t be made active again; create a new one instead.
        </p>
        <PaymentsError error={archive.error} context="package" />
      </ConfirmDialog>
    </Card>
  );
}

// ---------------------------------------------------------------- create / edit

interface ExtraRow {
  key: number;
  currency: string;
  amount: string;
}

const planDefaults = (plan: WebPlan) => ({ name: WEB_PLAN_LABEL[plan], trial: plan === "annual" ? "7" : "0", sort: String(WEB_PLANS.indexOf(plan)) });

/** Whole number from min to max, or null. */
const intIn = (raw: string, min: number, max: number) => {
  const n = Number(raw);
  return raw.trim() !== "" && Number.isInteger(n) && n >= min && n <= max ? n : null;
};

const amountHint = (currency: string) => {
  const exp = currencyExponent(currency);
  const code = currency.toUpperCase();
  return exp === 0 ? `${code} has no decimals: type 800 for ${formatMoney(800, currency)}.` : `In ${code}, e.g. 5.99.`;
};

function PackageDialog({ mode, packages, editing, onClose }: { mode: PaymentMode; packages: Package[]; editing: Package | null; onClose: () => void }) {
  const create = useCreatePackage();
  const update = useUpdatePackage();
  const mutation = editing ? update : create;
  const activeFor = (plan: WebPlan) => packages.find((x) => x.plan === plan && isLive(x) && x.id !== editing?.id) ?? null;
  const firstFree = WEB_PLANS.find((pl) => !activeFor(pl)) ?? "monthly";

  const [plan, setPlan] = useState<WebPlan>(editing?.plan ?? firstFree);
  const [name, setName] = useState(editing?.name ?? planDefaults(plan).name);
  const [description, setDescription] = useState(editing?.description ?? "");
  const [badge, setBadge] = useState(editing?.badge ?? (plan === "annual" ? "Best value" : ""));
  const [amount, setAmount] = useState(editing ? fromMinor(editing.amount, editing.currency) : "");
  const [currency, setCurrency] = useState(editing?.currency ?? "usd");
  const [extras, setExtras] = useState<ExtraRow[]>(() => (editing?.currencyOptions ?? []).map((o, i) => ({ key: i, currency: o.currency, amount: fromMinor(o.amount, o.currency) })));
  const [nextKey, setNextKey] = useState(extras.length);
  const [trial, setTrial] = useState(editing ? String(editing.trialDays) : planDefaults(plan).trial);
  const [sort, setSort] = useState(editing ? String(editing.sort) : planDefaults(plan).sort);
  const [active, setActive] = useState(editing ? editing.active : !activeFor(plan));

  const other = activeFor(plan);
  const lifetime = plan === "lifetime";

  const changePlan = (next: WebPlan) => {
    const before = planDefaults(plan);
    const after = planDefaults(next);
    if (name === before.name) setName(after.name);
    if (trial === before.trial || next === "lifetime") setTrial(after.trial);
    if (sort === before.sort) setSort(after.sort);
    if (badge === (plan === "annual" ? "Best value" : "")) setBadge(next === "annual" ? "Best value" : "");
    setActive(!activeFor(next));
    setPlan(next);
  };

  // ---- checks
  const nameProblem = !name.trim() ? "Give it a name." : name.trim().length > LIMITS.name ? `At most ${LIMITS.name} characters.` : null;
  const priceProblem = amountProblem(amount, currency);
  const minor = priceProblem ? null : toMinor(amount, currency);
  const extraProblems = extras.map((x, i) => {
    if (x.currency === currency) return `${x.currency.toUpperCase()} is already the main currency.`;
    if (extras.findIndex((y) => y.currency === x.currency) !== i) return `${x.currency.toUpperCase()} appears twice.`;
    return amountProblem(x.amount, x.currency);
  });
  const trialDays = lifetime ? 0 : intIn(trial, 0, LIMITS.trialDays);
  const sortN = intIn(sort, -1000, 1000);
  const conflict = active && other !== null;
  const valid =
    !nameProblem &&
    minor !== null &&
    extraProblems.every((x) => x === null) &&
    trialDays !== null &&
    sortN !== null &&
    description.length <= LIMITS.description &&
    badge.trim().length <= LIMITS.badge &&
    !conflict;

  const currencyOptions: CurrencyOption[] = extras.map((x) => ({ currency: x.currency, amount: toMinor(x.amount, x.currency) ?? 0 }));
  const priceChanged =
    editing !== null &&
    minor !== null &&
    (minor !== editing.amount || currency !== editing.currency || JSON.stringify(currencyOptions) !== JSON.stringify(editing.currencyOptions));

  const submit = () => {
    if (!valid || minor === null || trialDays === null || sortN === null) return;
    if (!editing) {
      const input: PackageInput = {
        mode,
        plan,
        name: name.trim(),
        description: description.trim(),
        badge: badge.trim(),
        amount: minor,
        currency,
        currencyOptions,
        trialDays,
        sort: sortN,
        active,
      };
      create.mutate(input, { onSuccess: onClose });
      return;
    }
    const patch: PackagePatch = {};
    if (name.trim() !== editing.name) patch.name = name.trim();
    if (description.trim() !== editing.description) patch.description = description.trim();
    if (badge.trim() !== editing.badge) patch.badge = badge.trim();
    if (minor !== editing.amount) patch.amount = minor;
    if (currency !== editing.currency) patch.currency = currency;
    if (JSON.stringify(currencyOptions) !== JSON.stringify(editing.currencyOptions)) patch.currencyOptions = currencyOptions;
    if (trialDays !== editing.trialDays) patch.trialDays = trialDays;
    if (sortN !== editing.sort) patch.sort = sortN;
    if (active !== editing.active) patch.active = active;
    if (Object.keys(patch).length === 0) return onClose();
    update.mutate({ id: editing.id, mode, patch }, { onSuccess: onClose });
  };

  const currencyChoices = (selected: string) => (CURRENCIES.some((c) => c.code === selected) ? CURRENCIES : [{ code: selected, name: currencyName(selected) }, ...CURRENCIES]);

  return (
    <Dialog
      open
      title={editing ? `Edit “${editing.name}”` : `New ${mode} package`}
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid} busy={mutation.isPending} onClick={submit} data-testid="save-package">
            {editing ? "Save changes" : "Create package"}
          </Button>
        </>
      }
    >
      <form
        className={p.form}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        data-testid="package-form"
      >
        <div>
          <span className="monoLabel">Plan</span>
          <div className={p.labelled}>
            {editing ? (
              <span>
                <strong>{WEB_PLAN_LABEL[plan]}</strong> <span className={pages.faint}>· {PLAN_INTERVAL[plan]} · the plan can&rsquo;t change</span>
              </span>
            ) : (
              <Segmented label="Plan" value={plan} options={WEB_PLANS.map((pl) => ({ value: pl, label: WEB_PLAN_LABEL[pl] }))} onChange={changePlan} />
            )}
          </div>
        </div>
        <div className={p.row}>
          <Field label="Name" hint="Shown on the web paywall and in Stripe.">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={LIMITS.name} aria-invalid={nameProblem ? true : undefined} data-autofocus data-testid="package-name" />
            {nameProblem ? <span className={pages.fieldError}>{nameProblem}</span> : null}
          </Field>
          <Field label="Badge" hint="Optional, e.g. Best value.">
            <TextInput value={badge} onChange={(e) => setBadge(e.target.value)} maxLength={LIMITS.badge} data-testid="package-badge" />
          </Field>
        </div>
        <Field label="Description" hint="Optional. Shown under the plan.">
          <textarea className={p.textarea} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={LIMITS.description} rows={2} data-testid="package-description" />
        </Field>

        <Field label="Price" hint={amountHint(currency)}>
          <div className={p.priceRow}>
            <TextInput
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={currencyExponent(currency) === 0 ? "800" : "5.99"}
              aria-label="Price"
              aria-invalid={amount && priceProblem ? true : undefined}
              className={p.mono}
              data-testid="package-amount"
            />
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="Currency" data-testid="package-currency">
              {currencyChoices(currency).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code.toUpperCase()} · {c.name}
                </option>
              ))}
            </Select>
          </div>
          {amount && priceProblem ? <span className={pages.fieldError}>{priceProblem}</span> : null}
          {minor !== null ? (
            <span className={p.preview} data-testid="amount-preview">
              {formatMoney(minor, currency)} {PLAN_INTERVAL[plan]} · sent to Stripe as <code>{minor}</code> {currency.toUpperCase()}
              {currencyExponent(currency) === 0 ? " (no minor unit)" : " minor units"}
            </span>
          ) : null}
        </Field>

        <div>
          <span className="monoLabel">Extra currencies</span>
          <div className={`${p.extras} ${p.labelled}`} data-testid="extra-currencies">
            {extras.map((x, i) => (
              <div key={x.key}>
                <div className={p.extra}>
                  <Select
                    value={x.currency}
                    onChange={(e) => setExtras(extras.map((y) => (y.key === x.key ? { ...y, currency: e.target.value } : y)))}
                    aria-label={`Extra currency ${i + 1}`}
                  >
                    {currencyChoices(x.currency).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code.toUpperCase()} · {c.name}
                      </option>
                    ))}
                  </Select>
                  <TextInput
                    inputMode="decimal"
                    value={x.amount}
                    onChange={(e) => setExtras(extras.map((y) => (y.key === x.key ? { ...y, amount: e.target.value } : y)))}
                    aria-label={`Price in ${x.currency.toUpperCase()}`}
                    aria-invalid={x.amount && extraProblems[i] ? true : undefined}
                    className={p.mono}
                  />
                  <Button size="sm" variant="ghost" onClick={() => setExtras(extras.filter((y) => y.key !== x.key))} aria-label={`Remove ${x.currency.toUpperCase()}`}>
                    Remove
                  </Button>
                </div>
                {x.amount && extraProblems[i] ? <span className={pages.fieldError}>{extraProblems[i]}</span> : null}
              </div>
            ))}
            {extras.length < LIMITS.extraCurrencies ? (
              <div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const used = new Set([currency, ...extras.map((x) => x.currency)]);
                    setExtras([...extras, { key: nextKey, currency: CURRENCIES.find((c) => !used.has(c.code))?.code ?? "eur", amount: "" }]);
                    setNextKey(nextKey + 1);
                  }}
                  data-testid="add-currency"
                >
                  Add currency
                </Button>
              </div>
            ) : null}
            <span className={p.toggleNote}>Stripe charges these prices to customers in those currencies; everyone else pays the main price.</span>
          </div>
        </div>

        <div className={p.row}>
          <Field label="Trial days" hint={lifetime ? "Lifetime is a one-time payment, so it has no trial." : "0 for none. Applied at checkout; no new price."}>
            <TextInput
              inputMode="numeric"
              value={lifetime ? "0" : trial}
              onChange={(e) => setTrial(e.target.value)}
              disabled={lifetime}
              aria-invalid={!lifetime && trialDays === null ? true : undefined}
              className={p.number}
              data-testid="package-trial"
            />
            {!lifetime && trialDays === null ? <span className={pages.fieldError}>A whole number from 0 to {LIMITS.trialDays}.</span> : null}
          </Field>
          <Field label="Sort" hint="Lower comes first on the paywall.">
            <TextInput inputMode="numeric" value={sort} onChange={(e) => setSort(e.target.value)} aria-invalid={sortN === null ? true : undefined} className={p.number} data-testid="package-sort" />
            {sortN === null ? <span className={pages.fieldError}>A whole number.</span> : null}
          </Field>
        </div>

        <div className={p.toggleRow}>
          <Switch checked={active} label="Active" disabled={!active && other !== null} onClick={() => setActive(!active)} testId="package-active" />
          <span>
            <strong>{active ? "Active" : "Inactive"}</strong>
            <span className={p.toggleNote} data-testid="active-note">
              {other
                ? `“${other.name}” is the active ${WEB_PLAN_LABEL[plan].toLowerCase()} package in ${mode} mode. Only one can be active: archive it or make it inactive first.`
                : active
                  ? "The web offers it as soon as it's saved."
                  : "Saved in Stripe but not offered on the web."}
            </span>
          </span>
        </div>

        {priceChanged ? (
          <p className={p.warning} role="note" data-testid="price-warning">
            Changing the price creates a new Stripe price and archives the old one. Existing subscribers keep the old price until they change plan; new
            checkouts use the new one.
          </p>
        ) : null}
        <PaymentsError error={mutation.error} context="package" testId="package-error" />
      </form>
    </Dialog>
  );
}
